import CheckRounded from "@mui/icons-material/CheckRounded";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import {
  Alert, Box, Button, Card, CardActions, CardContent, Chip,
  CircularProgress, Divider, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { FacilityPlaceClaimResponse, FacilityPlaceResponse } from "@bw/shared/types/generated";
import { PlatformPage } from "@/components/PlatformPage";
import {
  decideFacilityPlace, decideFacilityPlaceClaim,
  listFacilityPlaceClaims, listFacilityPlaces,
} from "../api";

type Queue = "places" | "claims";

function addressLabel(place: FacilityPlaceResponse) {
  return [place.address.line1, place.address.city, place.address.state, place.address.postal_code]
    .filter(Boolean).join(", ");
}

export function PlaceReviewPage() {
  const [queue, setQueue] = useState<Queue>("places");
  const client = useQueryClient();
  const places = useQuery({ queryKey: ["platform", "facility-places", "pending"], queryFn: () => listFacilityPlaces() });
  const claims = useQuery({ queryKey: ["platform", "facility-place-claims", "pending"], queryFn: () => listFacilityPlaceClaims() });
  const placeDecision = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approved" | "rejected" }) =>
      decideFacilityPlace(id, { decision, note: null }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["platform", "facility-places"] }),
  });
  const claimDecision = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approved" | "rejected" }) =>
      decideFacilityPlaceClaim(id, { decision, note: null }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["platform", "facility-place-claims"] }),
  });
  const active = queue === "places" ? places : claims;
  const failure = placeDecision.error ?? claimDecision.error;

  return (
    <PlatformPage title="Community places">
      <Stack spacing={2.5}>
        <Box>
          <Typography color="text.secondary">
            Review player-added places and verified-organization ownership claims.
            Approval of a place never enables bookings or payments by itself.
          </Typography>
        </Box>
        <Tabs value={queue} onChange={(_event, next: Queue) => setQueue(next)}>
          <Tab value="places" label={`Place submissions (${places.data?.length ?? 0})`} />
          <Tab value="claims" label={`Business claims (${claims.data?.length ?? 0})`} />
        </Tabs>
        {failure ? <Alert severity="error">{failure instanceof Error ? failure.message : "The decision could not be saved."}</Alert> : null}
        {active.isLoading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box> : null}
        {active.isError ? <Alert severity="error" action={<Button onClick={() => void active.refetch()}>Retry</Button>}>Couldn&apos;t load this review queue.</Alert> : null}
        {!active.isLoading && !active.isError && (active.data?.length ?? 0) === 0 ? (
          <Card variant="outlined"><CardContent sx={{ py: 6, textAlign: "center" }}>
            <CheckRounded color="success" sx={{ fontSize: 44 }} />
            <Typography variant="h5" sx={{ mt: 1 }}>Queue is clear</Typography>
            <Typography color="text.secondary">There are no pending {queue === "places" ? "place submissions" : "ownership claims"}.</Typography>
          </CardContent></Card>
        ) : null}
        {queue === "places" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
            {(places.data ?? []).map((place) => (
              <Card key={place.place_id} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ display: "grid", placeItems: "center", width: 44, height: 44, borderRadius: 2, bgcolor: "action.hover" }}><LocationOnOutlined /></Box>
                    <Box sx={{ minWidth: 0 }}><Typography variant="h6">{place.name}</Typography><Typography color="text.secondary">{addressLabel(place)}</Typography></Box>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1}><Chip size="small" label="Player submitted" /><Chip size="small" variant="outlined" label="No commercial access" /></Stack>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button color="error" startIcon={<CloseRounded />} disabled={placeDecision.isPending} onClick={() => placeDecision.mutate({ id: place.place_id, decision: "rejected" })}>Reject</Button>
                  <Button variant="contained" startIcon={<CheckRounded />} disabled={placeDecision.isPending} onClick={() => placeDecision.mutate({ id: place.place_id, decision: "approved" })}>Approve place</Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {(claims.data ?? []).map((claim: FacilityPlaceClaimResponse) => (
              <Card key={claim.claim_id} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ display: "grid", placeItems: "center", width: 44, height: 44, borderRadius: 2, bgcolor: "action.hover", flexShrink: 0 }}><StorefrontOutlined /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6">{claim.place_name ?? "Claimed place"}</Typography>
                      <Typography variant="body2" color="text.secondary">{claim.organization_name ?? "Organization details unavailable"}</Typography>
                      <Typography sx={{ mt: 1.5 }}>{claim.evidence}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}><Button color="error" disabled={claimDecision.isPending} onClick={() => claimDecision.mutate({ id: claim.claim_id, decision: "rejected" })}>Reject</Button><Button variant="contained" disabled={claimDecision.isPending} onClick={() => claimDecision.mutate({ id: claim.claim_id, decision: "approved" })}>Approve claim</Button></CardActions>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </PlatformPage>
  );
}
